import { useEffect, useState } from 'react';

const DESKTOP_TOP_CUSHION = 0;

export const useHeaderOffset = () => {
  const getComputedHeaderOffset = (header: HTMLElement | null, isCurrentlyDesktop: boolean) => {
    if (!header) return 0;

    const rect = header.getBoundingClientRect();
    const headerIsHidden = isCurrentlyDesktop && (
      header.classList.contains('-translate-y-full') || rect.bottom <= 0
    );

    if (headerIsHidden) return DESKTOP_TOP_CUSHION;

    // `bottom` is more reliable than `height` when the header is sticky/transformed.
    const occupiedTop = Math.max(0, rect.bottom);
    if (isCurrentlyDesktop) return occupiedTop + DESKTOP_TOP_CUSHION;

    // Usar rect.height en mobile (estable) en lugar de rect.bottom que
    // fluctúa con la URL bar de Chrome mobile.
    const mobileOffset = rect.height > 0 ? rect.height : occupiedTop;
    return mobileOffset > 0 ? mobileOffset : 0;
  };

  // Initialize with reasonable defaults to prevent initial 0 values
  const [headerHeight, setHeaderHeight] = useState<number>(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
    const header = document.querySelector('header.app-header') as HTMLElement | null;
    return getComputedHeaderOffset(header, window.innerWidth >= 1024);
  });
  const [isDesktop, setIsDesktop] = useState<boolean>(typeof window !== 'undefined' ? window.innerWidth >= 1024 : false);
  const [bottomNavHeight, setBottomNavHeight] = useState<number>(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return 0;
    const nav = document.querySelector('nav.app-bottom-nav') as HTMLElement | null;
    const h = nav ? nav.getBoundingClientRect().height : 0;
    return window.innerWidth < 1024 ? h : 0;
  });

  const update = () => {
    if (typeof document === 'undefined') return;
    
    const getHeader = () => document.querySelector('header.app-header') as HTMLElement | null;
    const getNav = () => document.querySelector('nav.app-bottom-nav') as HTMLElement | null;
    
    const header = getHeader();
    const nav = getNav();
    const navH = nav ? nav.getBoundingClientRect().height : 0;
    const isCurrentlyDesktop = window.innerWidth >= 1024;
    setHeaderHeight(getComputedHeaderOffset(header, isCurrentlyDesktop));
    
    setIsDesktop(isCurrentlyDesktop);
    setBottomNavHeight(window.innerWidth < 1024 ? navH : 0);
  };

  useEffect(() => {
    update();

    // Force multiple updates after different delays to ensure accurate measurement
    // Safari may need more time to lay out elements correctly
    const timeoutIds: NodeJS.Timeout[] = [];
    
    const delays = [50, 100, 150, 300]; // Progressive delays
    delays.forEach(delay => {
      const id = setTimeout(update, delay);
      timeoutIds.push(id);
    });

    const getHeader = () => document.querySelector('header.app-header') as HTMLElement | null;
    const getNav = () => document.querySelector('nav.app-bottom-nav') as HTMLElement | null;
    const header = getHeader();
    const nav = getNav();
    let ro: ResizeObserver | null = null;
    let mo: MutationObserver | null = null;

    try {
      if ((header || nav) && typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(update);
        if (header) ro.observe(header);
        if (nav) ro.observe(nav);
      }
    } catch (e) {
      // ignore
    }

    try {
      if (header && typeof MutationObserver !== 'undefined') {
        mo = new MutationObserver(update);
        mo.observe(header, { attributes: true, childList: true, subtree: true });
      }
    } catch (e) {
      // ignore
    }

    window.addEventListener('resize', update);
    // Escuchar evento global para recalcular antes de abrir sidebars
    window.addEventListener('recalculate-header', update);
    // Solo escuchar resize de visualViewport (NO scroll) — el scroll de
    // visualViewport en Chrome mobile dispara en cada frame cuando la URL bar
    // aparece/desaparece, causando setState continuos y jitter de layout.
    window.visualViewport?.addEventListener('resize', update);

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
      window.removeEventListener('resize', update);
      window.removeEventListener('recalculate-header', update);
      window.visualViewport?.removeEventListener('resize', update);
      try { ro?.disconnect(); } catch (e) {}
      try { mo?.disconnect(); } catch (e) {}
    };
  }, []);

    return { headerHeight, isDesktop, bottomNavHeight, recalculate: update } as const;
};

export default useHeaderOffset;
